import { MapPin, Phone } from 'lucide-react'
import * as m from '~/paraglide/messages'
import { countryDisplayName } from '~/shared/lib/countries'
import { scrollToFormField } from '~/shared/lib/form-utils'
import { cn } from '~/shared/lib/utils'
import { Badge } from '~/shared/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'

interface ListingPreviewProps {
  name: string
  description: string
  location: {
    name?: string
    street: string
    city: string
    country: string
  }
  phone?: string
  coverImageUrl?: string
  onFieldClick?: (fieldName: string) => void
}

export function ListingPreview({
  name,
  description,
  location,
  phone,
  coverImageUrl,
  onFieldClick,
}: ListingPreviewProps) {
  const handleClick = (fieldName: string) => {
    if (!onFieldClick) return
    onFieldClick(fieldName)
    scrollToFormField(fieldName)
  }

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => handleClick('coverImageUrl')}
        className={cn(
          'w-full transition-opacity',
          onFieldClick && 'cursor-pointer hover:opacity-80',
        )}
      >
        {coverImageUrl ? (
          <div className="aspect-[2/1] w-full overflow-hidden bg-muted">
            <img
              src={coverImageUrl}
              alt={name || m.listingPreview()}
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[2/1] w-full bg-gradient-to-br from-blue-100 to-cyan-100 dark:from-blue-950 dark:to-cyan-950 flex items-center justify-center">
            <MapPin className="h-8 w-8 text-blue-300 dark:text-blue-700" />
          </div>
        )}
      </button>
      <CardHeader className="p-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={() => handleClick('name')}
            className={cn(
              'text-left transition-colors',
              onFieldClick && 'cursor-pointer hover:text-blue-600',
            )}
          >
            <CardTitle className="line-clamp-2 text-sm">{name || m.businessName()}</CardTitle>
          </button>
          <Badge variant="secondary" className="text-xs shrink-0">
            {m.draft()}
          </Badge>
        </div>

        <button
          type="button"
          onClick={() => handleClick('description')}
          className={cn(
            'text-left transition-colors',
            onFieldClick && 'cursor-pointer hover:text-blue-600',
          )}
        >
          <CardDescription className="line-clamp-2 text-xs">
            {description || m.businessDescriptionWillAppearHere()}
          </CardDescription>
        </button>
      </CardHeader>
      <CardContent className="space-y-2 p-3 pt-0">
        <button
          type="button"
          onClick={() => handleClick('location.city')}
          className={cn(
            'flex items-center gap-2 text-xs w-full text-left transition-colors',
            onFieldClick && 'cursor-pointer hover:text-blue-600',
          )}
        >
          <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="truncate">
            {location.city && location.country
              ? `${location.city}, ${countryDisplayName(location.country)}`
              : m.locationNotSet()}
          </span>
        </button>

        {phone && (
          <button
            type="button"
            onClick={() => handleClick('phone')}
            className={cn(
              'flex items-center gap-2 text-xs w-full text-left transition-colors',
              onFieldClick && 'cursor-pointer hover:text-blue-600',
            )}
          >
            <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="truncate">{phone}</span>
          </button>
        )}
      </CardContent>
    </Card>
  )
}
